import * as React from 'react';

import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { BasicMenuItemOption, Menu } from 'ui/menu';

import { getFeatureFlags } from 'core/userContext';
import { useCampaignProjectLabels } from 'modules/campaigns/editor/hooks';
import { CampaignLabel } from 'modules/domains/campaign';
import { getFilteredTriggerList } from 'modules/workflowNodeEditors/components/StartNodeEditor/EventEntrySource/EventEntrySource.constants';
import { useSelector } from 'react-redux';
import {
  FILTER_MENU_ID_PREFIXES,
  OVERWRITTEN_WORKFLOW_STATUS_FILTER_WITH_DRAFTS_OPTIONS,
  WORKFLOW_STATUS_FILTER_WITH_DRAFTS_OPTIONS,
  WORKFLOW_TRIGGER_EVENT_DISPLAY_NAMES,
  WORKFLOW_TRIGGER_EVENTS,
  WORKFLOW_TRIGGERS_DISPLAY_NAMES,
} from '../constants';
import { WorkflowCreatorOrModifierUser, WorkflowStatusFilterOption } from '../types';

const { useCallback, useMemo } = React;

type FiltersMenuProps = {
  setStatusFilterIds: (statusFilterIds: string[]) => void;
  statusFilterIds: string[];
  workflowTypeFilterIds: string[];
  setWorkflowTypeFilterIds: (workflowTypeFilterIds: string[]) => void;
  triggerTypeFilterIds: string[];
  setTriggerTypeFilterIds: (triggerTypeFilterIds: string[]) => void;
  workflowCreators: WorkflowCreatorOrModifierUser[];
  workflowModifiers: WorkflowCreatorOrModifierUser[];
  creatorFilterIds: string[];
  setCreatorFilterIds: (creatorFilterIds: string[]) => void;
  modifierFilterIds: string[];
  setModifierFilterIds: (modifierFilterIds: string[]) => void;
  labelFiltersIds: string[];
  setLabelFilterIds: (labelFilterIds: string[]) => void;
  journeyVisitorTrackingIds: string[];
  setJourneyVisitorTrackingIds: (journeyConversionsIds: string[]) => void;
};

const prefixIdWith = (id: string, prefix: string) => `${prefix}${id}`;

const prefixIdsWith = (arr: { id: string }[], prefix: string) =>
  arr.map(({ id, ...otherProperties }) => ({ id: prefixIdWith(id, prefix), ...otherProperties }));

const stripIdPrefix = (prefixedId: string, prefix: string) =>
  prefixedId.startsWith(prefix) ? prefixedId.substr(prefix.length) : prefixedId;

export const getEntrySourceFilterOptions = (featureFlags: Record<string, boolean>) => {
  const eventsList = getFilteredTriggerList(featureFlags).map((id) => {
    const triggerEventKey = id.endsWith('Trigger') ? id.slice(0, id.length - 7) : id;
    return {
      id: triggerEventKey,
      label: WORKFLOW_TRIGGER_EVENT_DISPLAY_NAMES[triggerEventKey as WORKFLOW_TRIGGER_EVENTS],
    };
  });
  const otherTriggersList = Object.entries(WORKFLOW_TRIGGERS_DISPLAY_NAMES).map(([id, label]) => ({
    id,
    label,
  }));
  return [...otherTriggersList, ...eventsList];
};

/**
 * Returns Workflow status filters with Drafts
 * Updates status filter labels that are different from tab labels
 */
const getWorkflowStatusFilterWithDraftsOptions = () =>
  WORKFLOW_STATUS_FILTER_WITH_DRAFTS_OPTIONS.reduce(
    (acc: any[], curr) => {
      const overwrite = OVERWRITTEN_WORKFLOW_STATUS_FILTER_WITH_DRAFTS_OPTIONS.find(
        ({ id }) => id === curr.id,
      );

      const option: WorkflowStatusFilterOption & { prefix?: string } = overwrite || curr;
      return option.id
        ? [
            ...acc,
            {
              id: prefixIdWith(option.id, option.prefix || FILTER_MENU_ID_PREFIXES.STATUS),
              label: option.label,
            },
          ]
        : acc;
    },
    [] as typeof WORKFLOW_STATUS_FILTER_WITH_DRAFTS_OPTIONS,
  );

const getOptions = (
  workflowCreators: WorkflowCreatorOrModifierUser[],
  workflowModifiers: WorkflowCreatorOrModifierUser[],
  labelFilters: CampaignLabel[],
  featureFlags: Record<string, boolean> = {},
): BasicMenuItemOption[] =>
  [
    {
      hasFilter: true,
      isVisible: featureFlags.JourneyLabels,
      id: 'Labels',
      isSelectMenu: true,
      label: 'Labels',
      options: prefixIdsWith(
        labelFilters.map(({ id, name }) => ({ id: `${id}`, label: name })),
        FILTER_MENU_ID_PREFIXES.LABEL,
      ),
    },
    {
      hasFilter: true,
      isVisible: true,
      id: 'status',
      isSelectMenu: true,
      label: 'Status',
      options: getWorkflowStatusFilterWithDraftsOptions(),
    },
    {
      hasFilter: true,
      isVisible: true,
      id: 'triggerType',
      isSelectMenu: true,
      label: 'Entry source',
      options: prefixIdsWith(
        getEntrySourceFilterOptions(featureFlags),
        FILTER_MENU_ID_PREFIXES.TRIGGER_TYPE,
      ),
    },
    {
      hasFilter: true,
      isVisible: workflowCreators.length > 0,
      id: 'creator',
      isSelectMenu: true,
      label: 'Created by',
      options: prefixIdsWith(
        workflowCreators.map(({ id, fullName }) => ({ id, label: fullName })),
        FILTER_MENU_ID_PREFIXES.CREATOR,
      ),
    },
    {
      hasFilter: true,
      isVisible: workflowCreators.length > 0,
      id: 'modifier',
      isSelectMenu: true,
      label: 'Modified by',
      options: prefixIdsWith(
        workflowModifiers.map(({ id, fullName }) => ({ id, label: fullName })),
        FILTER_MENU_ID_PREFIXES.MODIFIER,
      ),
    },
    {
      isVisible: featureFlags.InProgressTotalPublishedView,
      id: 'journey-visitor-tracking',
      isSelectMenu: true,
      label: 'Journey event tracking',
      options: prefixIdsWith(
        [
          { id: true, name: 'On' },
          { id: false, name: 'Off' },
        ].map(({ id, name }) => ({ id: `${id}`, label: name })),
        FILTER_MENU_ID_PREFIXES.JOURNEY_VISITOR_TRACKING,
      ),
    },
  ].filter(({ isVisible }) => isVisible);

export const FiltersMenu = ({
  statusFilterIds,
  setStatusFilterIds,
  workflowTypeFilterIds,
  setWorkflowTypeFilterIds,
  triggerTypeFilterIds,
  setTriggerTypeFilterIds,
  workflowCreators,
  workflowModifiers,
  creatorFilterIds,
  modifierFilterIds,
  setCreatorFilterIds,
  setModifierFilterIds,
  labelFiltersIds,
  setLabelFilterIds,
  journeyVisitorTrackingIds,
  setJourneyVisitorTrackingIds,
}: FiltersMenuProps) => {
  const featureFlags = useSelector(getFeatureFlags);
  const { results: campaignProjectLabels } = useCampaignProjectLabels();

  const handleSelectionChange = useCallback(
    (prefixedFilterId: string) => {
      if (prefixedFilterId.startsWith(FILTER_MENU_ID_PREFIXES.STATUS)) {
        const id = stripIdPrefix(prefixedFilterId, FILTER_MENU_ID_PREFIXES.STATUS);
        setStatusFilterIds(
          statusFilterIds.includes(id)
            ? statusFilterIds.filter((otherId) => otherId !== id)
            : [...statusFilterIds, id],
        );
      } else if (prefixedFilterId.startsWith(FILTER_MENU_ID_PREFIXES.WORKFLOW_TYPE)) {
        const id = stripIdPrefix(prefixedFilterId, FILTER_MENU_ID_PREFIXES.WORKFLOW_TYPE);
        setWorkflowTypeFilterIds(
          workflowTypeFilterIds.includes(id)
            ? workflowTypeFilterIds.filter((otherId) => otherId !== id)
            : [...workflowTypeFilterIds, id],
        );
      } else if (prefixedFilterId.startsWith(FILTER_MENU_ID_PREFIXES.TRIGGER_TYPE)) {
        const id = stripIdPrefix(prefixedFilterId, FILTER_MENU_ID_PREFIXES.TRIGGER_TYPE);
        setTriggerTypeFilterIds(
          triggerTypeFilterIds.includes(id)
            ? triggerTypeFilterIds.filter((otherId) => otherId !== id)
            : [...triggerTypeFilterIds, id],
        );
      } else if (prefixedFilterId.startsWith(FILTER_MENU_ID_PREFIXES.CREATOR)) {
        const id = stripIdPrefix(prefixedFilterId, FILTER_MENU_ID_PREFIXES.CREATOR);
        setCreatorFilterIds(
          creatorFilterIds.includes(id)
            ? creatorFilterIds.filter((otherId) => otherId !== id)
            : [...creatorFilterIds, id],
        );
      } else if (prefixedFilterId.startsWith(FILTER_MENU_ID_PREFIXES.MODIFIER)) {
        const id = stripIdPrefix(prefixedFilterId, FILTER_MENU_ID_PREFIXES.MODIFIER);
        setModifierFilterIds(
          modifierFilterIds.includes(id)
            ? modifierFilterIds.filter((otherId) => otherId !== id)
            : [...modifierFilterIds, id],
        );
      } else if (prefixedFilterId.startsWith(FILTER_MENU_ID_PREFIXES.LABEL)) {
        const id = stripIdPrefix(prefixedFilterId, FILTER_MENU_ID_PREFIXES.LABEL);
        setLabelFilterIds(
          labelFiltersIds.includes(id)
            ? labelFiltersIds.filter((otherId) => otherId !== id)
            : [...labelFiltersIds, id],
        );
      } else if (prefixedFilterId.startsWith(FILTER_MENU_ID_PREFIXES.JOURNEY_VISITOR_TRACKING)) {
        const id = stripIdPrefix(
          prefixedFilterId,
          FILTER_MENU_ID_PREFIXES.JOURNEY_VISITOR_TRACKING,
        );
        setJourneyVisitorTrackingIds(
          journeyVisitorTrackingIds.includes(id)
            ? journeyVisitorTrackingIds.filter((otherId) => otherId !== id)
            : [...journeyVisitorTrackingIds, id],
        );
      }
    },
    [
      setStatusFilterIds,
      setTriggerTypeFilterIds,
      workflowTypeFilterIds,
      setWorkflowTypeFilterIds,
      statusFilterIds,
      triggerTypeFilterIds,
      creatorFilterIds,
      modifierFilterIds,
      setCreatorFilterIds,
      setModifierFilterIds,
      labelFiltersIds,
      setLabelFilterIds,
      journeyVisitorTrackingIds,
      setJourneyVisitorTrackingIds,
    ],
  );

  const currentSelection = useMemo(() => {
    const flattenedFilters: string[] = [];
    if (labelFiltersIds.length) {
      labelFiltersIds.forEach((labelFilterId) => {
        flattenedFilters.push(prefixIdWith(labelFilterId, FILTER_MENU_ID_PREFIXES.LABEL));
      });
    }

    if (statusFilterIds.length) {
      statusFilterIds.forEach((statusFilterId) => {
        flattenedFilters.push(prefixIdWith(statusFilterId, FILTER_MENU_ID_PREFIXES.STATUS));
      });
    }

    if (workflowTypeFilterIds.length) {
      workflowTypeFilterIds.forEach((workflowTypeFilterId) => {
        flattenedFilters.push(
          prefixIdWith(workflowTypeFilterId, FILTER_MENU_ID_PREFIXES.WORKFLOW_TYPE),
        );
      });
    }

    if (triggerTypeFilterIds.length) {
      triggerTypeFilterIds.forEach((triggerTypeFilterId) =>
        flattenedFilters.push(
          prefixIdWith(triggerTypeFilterId, FILTER_MENU_ID_PREFIXES.TRIGGER_TYPE),
        ),
      );
    }

    if (creatorFilterIds.length) {
      creatorFilterIds.forEach((creatorFilterId) =>
        flattenedFilters.push(prefixIdWith(creatorFilterId, FILTER_MENU_ID_PREFIXES.CREATOR)),
      );
    }

    if (modifierFilterIds.length) {
      modifierFilterIds.forEach((modifierFilterId) =>
        flattenedFilters.push(prefixIdWith(modifierFilterId, FILTER_MENU_ID_PREFIXES.MODIFIER)),
      );
    }
    if (journeyVisitorTrackingIds.length) {
      journeyVisitorTrackingIds.forEach((journeyConversionsId) =>
        flattenedFilters.push(
          prefixIdWith(journeyConversionsId, FILTER_MENU_ID_PREFIXES.JOURNEY_VISITOR_TRACKING),
        ),
      );
    }

    return flattenedFilters;
  }, [
    statusFilterIds,
    triggerTypeFilterIds,
    creatorFilterIds,
    modifierFilterIds,
    workflowTypeFilterIds,
    labelFiltersIds,
    journeyVisitorTrackingIds,
  ]);

  const options = useMemo(
    () => getOptions(workflowCreators, workflowModifiers, campaignProjectLabels, featureFlags),
    [workflowCreators, workflowModifiers, campaignProjectLabels, featureFlags],
  );

  return (
    <Menu
      options={options}
      defaultSelectedOptionIds={currentSelection}
      id="workflow-index-filters"
      handleSelectionChange={handleSelectionChange}
    >
      <FilterMenuTriggerButton selectedFilterCount={currentSelection.length} />
    </Menu>
  );
};
